import { ClientTools } from '@elevenlabs/react';
import { AI_ONBOARDING_CLIENT_TOOL_PREFIX } from '@pharmaplan/common/aiOnboarding';

import { OnboardingClientTools } from './clientTools';

interface IClientToolExecutionCallbacks {
  onClientToolFinished: () => void;
  onClientToolStarted: () => void;
}

const workflowRoutedFailureTools = new Set<keyof OnboardingClientTools>([
  'handle_optional_resume',
  'save_onboarding_details',
  'save_profile_availability',
  'save_profile_contact_and_experience',
  'save_profile_qualifications',
  'submit_onboarding_profile',
  'switch_to_manual_mode',
  'upload_profile_picture',
  'upload_technician_diploma',
  'verify_pharmacist_license',
]);

const shouldRouteToolFailure = (
  toolName: keyof OnboardingClientTools,
) => {
  if (toolName === 'verify_pharmacist_license') {
    return false;
  }

  return workflowRoutedFailureTools.has(toolName);
};

export const isRecoverableElevenLabsClientToolError = (context: unknown) => {
  if (context === null || typeof context !== 'object') {
    return false;
  }

  const clientToolName = Reflect.get(context, 'clientToolName');

  if (
    typeof clientToolName !== 'string'
    || !clientToolName.startsWith(AI_ONBOARDING_CLIENT_TOOL_PREFIX)
  ) {
    return false;
  }

  const toolName = clientToolName.slice(
    AI_ONBOARDING_CLIENT_TOOL_PREFIX.length,
  ) as keyof OnboardingClientTools;

  return workflowRoutedFailureTools.has(toolName);
};

export const isUnrecoverableElevenLabsDisconnect = (details: unknown) => {
  if (typeof details !== 'string') {
    return false;
  }

  return /all llms have failed|llm cascade error/i.test(details);
};

const serializeToolResult = (
  toolName: keyof OnboardingClientTools,
  result: unknown,
) => {
  if (
    result
    && typeof result === 'object'
    && Reflect.get(result, 'success') === false
    && shouldRouteToolFailure(toolName)
  ) {
    throw new Error(JSON.stringify(result));
  }

  return JSON.stringify(result);
};

export const toElevenLabsClientTools = (
  onboardingClientTools: OnboardingClientTools,
  callbacks: IClientToolExecutionCallbacks,
): ClientTools => {
  const clientTools: ClientTools = {};

  Object.keys(onboardingClientTools).forEach((toolName) => {
    const onboardingClientTool = onboardingClientTools[
      toolName as keyof OnboardingClientTools
    ];

    clientTools[`${AI_ONBOARDING_CLIENT_TOOL_PREFIX}${toolName}`] = async (
      parameters,
    ) => {
      callbacks.onClientToolStarted();

      try {
        const result = await onboardingClientTool(parameters);

        return serializeToolResult(
          toolName as keyof OnboardingClientTools,
          result,
        );
      } finally {
        callbacks.onClientToolFinished();
      }
    };
  });

  return clientTools;
};
